/**
 * Burning Cash live ticker — drops onto Dashboard.
 *
 * Pulls the rate from /api/burning-cash/rate every 5s; animates a
 * tabular-num counter that visibly increments per second. Designed to
 * be a small inline component, not a full page.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Flame, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TICK_MS = 250; // 4× per second
const REFRESH_MS = 10000;

const BurningCashTicker = ({ compact = false }) => {
  const [rate, setRate] = useState({ usd_per_second: 0, usd_per_hour: 0, monthly_burn_usd: 0, recommendation_count: 0 });
  const [accumulated, setAccumulated] = useState(0);
  const lastTickRef = useRef(Date.now());

  const refresh = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` };
      const r = await fetch(`${API}/burning-cash/rate`, { headers });
      if (r.ok) {
        const data = await r.json();
        setRate(data);
      }
    } catch (e) {
      // Silent — keep the ticker showing the last rate
    }
  };

  // Refresh from server every 10s
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  // Local accumulation tick every 250ms
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      const delta = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      setAccumulated((a) => a + delta * rate.usd_per_second);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [rate.usd_per_second]);

  if (compact) {
    return (
      <Link to="/recommendations" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 border border-rose-200 hover:bg-rose-100 transition-colors" data-testid="burning-cash-pill">
        <Flame className="w-3.5 h-3.5 text-rose-500" />
        <span className="text-xs font-bold tabular-nums text-rose-700">${accumulated.toFixed(2)}</span>
        <span className="text-[10px] text-rose-600 uppercase tracking-wider">burning</span>
      </Link>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 bg-gradient-to-br from-rose-50 via-white to-amber-50 p-5 shadow-sm" data-testid="burning-cash-widget">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold tracking-widest text-rose-600 uppercase mb-1 flex items-center gap-1.5">
            <Flame className="w-3 h-3 text-rose-500 animate-pulse" /> Burning right now
          </div>
          <div className="text-4xl font-extrabold tabular-nums text-rose-700">${accumulated.toFixed(2)}</div>
          <div className="text-xs text-slate-600 mt-1">since you opened this page</div>
        </div>
        <Link to="/recommendations" className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white px-3 py-1.5 text-xs font-semibold transition-colors shadow-md shadow-rose-500/30" data-testid="burning-cash-fix-cta">
          <TrendingUp className="w-3.5 h-3.5" /> Stop the bleed
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-rose-200/60">
        <div>
          <div className="text-[10px] text-rose-700 uppercase font-bold tracking-wider">$/sec</div>
          <div className="text-sm font-mono text-slate-900">${rate.usd_per_second.toFixed(6)}</div>
        </div>
        <div>
          <div className="text-[10px] text-rose-700 uppercase font-bold tracking-wider">$/hr</div>
          <div className="text-sm font-mono text-slate-900">${rate.usd_per_hour.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] text-rose-700 uppercase font-bold tracking-wider">$/mo</div>
          <div className="text-sm font-mono text-slate-900">${rate.monthly_burn_usd.toLocaleString()}</div>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-3 italic">
        From {rate.recommendation_count} active recommendations not yet applied. Apply one to silence its contribution.
      </p>
    </div>
  );
};

export default BurningCashTicker;
